import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

interface AnimatedNumberProps {
  value: number
  /** Locale for `toLocaleString`. Default 'en-US'. */
  locale?: string
  /** Animation duration in ms. Default 1400. */
  duration?: number
  /** Optional prefix (e.g. "+"). */
  prefix?: string
  /** Optional suffix (e.g. "K"). */
  suffix?: string
}

/**
 * Animated counter that springs from 0 → `value` once when it scrolls into
 * view. Reduced-motion users see the final value instantly.
 */
export function AnimatedNumber({
  value,
  locale = 'en-US',
  duration = 1400,
  prefix = '',
  suffix = '',
}: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const [display, setDisplay] = useState(0)
  const reducedMotion = useReducedMotion()
  const startedRef = useRef(false)

  useEffect(() => {
    if (reducedMotion) {
      setDisplay(value)
      return
    }
    const el = ref.current
    if (!el) return

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true
            const start = performance.now()
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / duration)
              // ease-out cubic
              const eased = 1 - Math.pow(1 - t, 3)
              setDisplay(Math.round(value * eased))
              if (t < 1) requestAnimationFrame(tick)
            }
            requestAnimationFrame(tick)
            io.unobserve(el)
          }
        }
      },
      { threshold: 0.4 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [value, duration, reducedMotion])

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display.toLocaleString(locale)}
      {suffix}
    </span>
  )
}
